import React, { useEffect, useState } from 'react';
import styles from '../css-isolated/hr_tb_screening.module.css';


function TBScreening({ values = {}, onChange, isDarkMode = false, formState, activeSection: externalActiveSection, setActiveSection: externalSetActiveSection }) {
  // Active section state (1 = TB Screening Checklist, 2 = Assessment & Results, 3 = TB Guidelines)
  // Use external state if provided (from HRWizard), otherwise use internal state
  const [internalActiveSection] = useState(1);
  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;
  // Destructure or default the values for each section
  const checklist = values.checklist || {
    A: null,
    B: null,
    B_known: null,
    C_blood: null,
    C_weight: null,
    C_fever: null,
    D: null,
    E: null,
    F: null,
  };
  const remarks = values.remarks || {
    presumptiveTB: false,
    presumptiveDRTB: false,
    notTB: false,
    ongoing: false,
    other: false,
    otherText: ''
  };
  const finalResult = values.finalResult || {
    newCase: false,
    retreatment: false,
    negative: false,
    bc: false,
    cd: false
  };
  const tbOutcome = values.tbOutcome || '';
  const tbExposure = values.tbExposure || '';

  // Handlers update the parent wizard state
  const handleChecklist = (key, value) => {
    onChange && onChange({
      ...values,
      checklist: { ...checklist, [key]: value }
    });
  };
  const handleRemarks = (key, value) => {
    onChange && onChange({
      ...values,
      remarks: { ...remarks, [key]: value }
    });
  };
  // handleFinalResult removed (was unused)
  const handleTbOutcome = (val) => {
    onChange && onChange({
      ...values,
      tbOutcome: val
    });
  };
  const handleTbExposure = (val) => {
    onChange && onChange({
      ...values,
      tbExposure: val
    });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    // Let wizard handle submit
  };

  // Auto-select BMI < 18.5 radio button based on Physical Examination BMI
  useEffect(() => {
    // Get BMI from physical examination form state
    const bmi = parseFloat(formState?.physical?.vitals?.bmi);
    
    if (bmi && !isNaN(bmi)) {
      // Auto-select "Yes" if BMI < 18.5, "No" if BMI >= 18.5
      const shouldBeUnderweight = bmi < 18.5;
      
      // Update both BMI-related fields: B and C_weight
      // B is always auto-selected
      // C_weight is auto-selected only if BMI < 18.5 (locked to Yes)
      onChange && onChange({
        ...values,
        checklist: {
          ...checklist,
          B: shouldBeUnderweight,
          C_weight: shouldBeUnderweight ? true : checklist.C_weight // Only force true if BMI < 18.5
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState?.physical?.vitals?.bmi]); // Only run when BMI changes

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      <form onSubmit={handleSubmit} autoComplete="off" className={styles.contentWrapper}>
          {/* Section 1: TB Screening Checklist */}
          {activeSection === 1 && (
          <div className={styles.formSection} style={{ gridColumn: '1 / 3', gridRow: '1 / 2' }}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>TB Screening Checklist</h2>
            </div>
            <div className={styles.tbChecklist}>
            {/* Checklist Header */}
            <div className={`${styles.tbRow} ${styles.tbHeaderRow}`}>
              <div className={styles.tbLabel}></div>
              <div className={`${styles.tbRadio} ${styles.tbRadioHeader}`}>Yes</div>
              <div className={`${styles.tbRadio} ${styles.tbRadioHeader}`}>No</div>
            </div>
            {/* Checklist Items */}
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>A. Unexplained cough, any duration <span className={styles.requiredAsterisk}>*</span></span>
              </div>
              <div className={styles.tbRadio}><input type="radio" name="A" checked={checklist.A === true} onChange={() => handleChecklist('A', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="A" checked={checklist.A === false} onChange={() => handleChecklist('A', false)} /></div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>B. BMI {'<'} 18.5 <span className={styles.requiredAsterisk}>*</span></span>
                {formState?.physical?.vitals?.bmi && (
                  <span style={{fontSize: '12px', color: '#7091E6', marginLeft: '8px'}}>
                    (Current BMI: {formState.physical.vitals.bmi} - Auto-selected)
                  </span>
                )}
              </div>
              <div className={styles.tbRadio}>
                <input 
                  type="radio" 
                  name="B" 
                  checked={checklist.B === true} 
                  onChange={() => {}} 
                  disabled={true}
                  style={{cursor: 'not-allowed', opacity: 0.7}}
                />
              </div>
              <div className={styles.tbRadio}>
                <input 
                  type="radio" 
                  name="B" 
                  checked={checklist.B === false} 
                  onChange={() => {}} 
                  disabled={true}
                  style={{cursor: 'not-allowed', opacity: 0.7}}
                />
              </div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>Known medical conditions<span className={styles.requiredAsterisk}>*</span></span>
                <span className={styles.tbNote}>(Clinical High-risk Groups: HIV/AIDS, diabetes, end-stage renal disease, cancer, connective tissue diseases, autoimmune diseases, silicosis, gastrectomy, solid organ transplantation, prolonged systemic steroids)</span>
              </div>
              <div className={styles.tbRadio}><input type="radio" name="B_known" checked={checklist.B_known === true} onChange={() => handleChecklist('B_known', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="B_known" checked={checklist.B_known === false} onChange={() => handleChecklist('B_known', false)} /></div>
            </div>
            {/* C. Section - Three checklist items */}
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>C. Blood-streaked sputum <span className={styles.requiredAsterisk}>*</span></span>
              </div>
              <div className={styles.tbRadio}><input type="radio" name="C_blood" checked={checklist.C_blood === true} onChange={() => handleChecklist('C_blood', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="C_blood" checked={checklist.C_blood === false} onChange={() => handleChecklist('C_blood', false)} /></div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>Weight loss or BMI {'<'} 18.5 <span className={styles.requiredAsterisk}>*</span></span>
                {formState?.physical?.vitals?.bmi && (
                  <span style={{fontSize: '12px', color: '#7091E6', marginLeft: '8px'}}>
                    {parseFloat(formState.physical.vitals.bmi) < 18.5 
                      ? '(Current BMI: ' + formState.physical.vitals.bmi + ' - Locked to Yes)'
                      : '(Current BMI: ' + formState.physical.vitals.bmi + ' - Editable for weight loss)'}
                  </span>
                )}
              </div>
              <div className={styles.tbRadio}>
                <input 
                  type="radio" 
                  name="C_weight" 
                  checked={checklist.C_weight === true} 
                  onChange={() => handleChecklist('C_weight', true)} 
                  disabled={formState?.physical?.vitals?.bmi && parseFloat(formState.physical.vitals.bmi) < 18.5}
                  style={formState?.physical?.vitals?.bmi && parseFloat(formState.physical.vitals.bmi) < 18.5 
                    ? {cursor: 'not-allowed', opacity: 0.7} 
                    : {}}
                />
              </div>
              <div className={styles.tbRadio}>
                <input 
                  type="radio" 
                  name="C_weight" 
                  checked={checklist.C_weight === false} 
                  onChange={() => handleChecklist('C_weight', false)} 
                  disabled={formState?.physical?.vitals?.bmi && parseFloat(formState.physical.vitals.bmi) < 18.5}
                  style={formState?.physical?.vitals?.bmi && parseFloat(formState.physical.vitals.bmi) < 18.5 
                    ? {cursor: 'not-allowed', opacity: 0.7} 
                    : {}}
                />
              </div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>Persistent fever <span className={styles.requiredAsterisk}>*</span></span>
              </div>
              <div className={styles.tbRadio}><input type="radio" name="C_fever" checked={checklist.C_fever === true} onChange={() => handleChecklist('C_fever', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="C_fever" checked={checklist.C_fever === false} onChange={() => handleChecklist('C_fever', false)} /></div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}>
                <span>D. Chest x-ray suggestive of TB <span className={styles.requiredAsterisk}>*</span></span>
              </div>
              <div className={styles.tbRadio}><input type="radio" name="D" checked={checklist.D === true} onChange={() => handleChecklist('D', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="D" checked={checklist.D === false} onChange={() => handleChecklist('D', false)} /></div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}><span>E. History of previous TB treatment<span className={styles.requiredAsterisk}>*</span></span>
                <input
                  type="text"
                  className={styles.tbInput}
                  placeholder="If outcome known, specify"
                  value={tbOutcome}
                  onChange={e => handleTbOutcome(e.target.value)}
                  disabled={checklist.E !== true}
                />
              </div>
              <div className={styles.tbRadio}><input type="radio" name="E" checked={checklist.E === true} onChange={() => handleChecklist('E', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="E" checked={checklist.E === false} onChange={() => handleChecklist('E', false)} /></div>
            </div>
            <div className={styles.tbRow}>
              <div className={`${styles.tbLabel} ${styles.requiredTbLabel}`}><span>F. Exposure to TB case<span className={styles.requiredAsterisk}>*</span></span>
                <input
                  type="text"
                  className={styles.tbInput}
                  placeholder="If known, specify"
                  value={tbExposure}
                  onChange={e => handleTbExposure(e.target.value)}
                  disabled={checklist.F !== true}
                />
              </div>
              <div className={styles.tbRadio}><input type="radio" name="F" checked={checklist.F === true} onChange={() => handleChecklist('F', true)} /></div>
              <div className={styles.tbRadio}><input type="radio" name="F" checked={checklist.F === false} onChange={() => handleChecklist('F', false)} /></div>
            </div>
              </div>
            </div>
          )}

          {/* Section 2: Assessment & Results and TB Guidelines (side-by-side) */}
          {activeSection === 2 && (
          <>
          <div className={styles.formSection} style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Assessment & Results</h2>
            </div>
            <div className={styles.formGrid}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Remarks:</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioOption}><input type="radio" name="remarks" checked={remarks.presumptiveTB} onChange={() => onChange({ ...values, remarks: { presumptiveTB: true, presumptiveDRTB: false, notTB: false, ongoing: false, other: false, otherText: '' } })} className={styles.radioInput} /> Presumptive TB</label>
                      <label className={styles.radioOption}><input type="radio" name="remarks" checked={remarks.presumptiveDRTB} onChange={() => onChange({ ...values, remarks: { presumptiveTB: false, presumptiveDRTB: true, notTB: false, ongoing: false, other: false, otherText: '' } })} className={styles.radioInput} /> Presumptive DR-TB</label>
                      <label className={styles.radioOption}><input type="radio" name="remarks" checked={remarks.notTB} onChange={() => onChange({ ...values, remarks: { presumptiveTB: false, presumptiveDRTB: false, notTB: true, ongoing: false, other: false, otherText: '' } })} className={styles.radioInput} /> Not TB</label>
                      <label className={styles.radioOption}><input type="radio" name="remarks" checked={remarks.ongoing} onChange={() => onChange({ ...values, remarks: { presumptiveTB: false, presumptiveDRTB: false, notTB: false, ongoing: true, other: false, otherText: '' } })} className={styles.radioInput} /> On-going TB treatment <span className={styles.tbNote}>(Ask for NTP Referral Form)</span></label>
                      <div className={styles.radioOptionWithInput}>
                        <label className={styles.radioOption}>
                          <input type="radio" name="remarks" checked={remarks.other} onChange={() => onChange({ ...values, remarks: { presumptiveTB: false, presumptiveDRTB: false, notTB: false, ongoing: false, other: true, otherText: remarks.otherText } })} className={styles.radioInput} /> 
                          Other follow-up, specify:
                        </label>
                        <input type="text" className={styles.formInput} style={{marginLeft: '1.7rem', marginTop: '0.25rem', width: 'calc(100% - 1.7rem)'}} value={remarks.otherText} onChange={e => handleRemarks('otherText', e.target.value)} disabled={!remarks.other} placeholder="Specify other follow-up" />
                      </div>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Final Result:</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioOption}><input type="radio" name="finalResult" checked={finalResult.newCase} onChange={() => onChange({ ...values, finalResult: { newCase: true, retreatment: false, negative: false, bc: false, cd: false } })} className={styles.radioInput} /> New Case</label>
                      <label className={styles.radioOption}><input type="radio" name="finalResult" checked={finalResult.retreatment} onChange={() => onChange({ ...values, finalResult: { newCase: false, retreatment: true, negative: false, bc: false, cd: false } })} className={styles.radioInput} /> Retreatment</label>
                      <label className={styles.radioOption}><input type="radio" name="finalResult" checked={finalResult.negative} onChange={() => onChange({ ...values, finalResult: { newCase: false, retreatment: false, negative: true, bc: false, cd: false } })} className={styles.radioInput} /> Negative</label>
                      <label className={styles.radioOption}><input type="radio" name="finalResult" checked={finalResult.bc} onChange={() => onChange({ ...values, finalResult: { newCase: false, retreatment: false, negative: false, bc: true, cd: false } })} className={styles.radioInput} /> BC</label>
                      <label className={styles.radioOption}><input type="radio" name="finalResult" checked={finalResult.cd} onChange={() => onChange({ ...values, finalResult: { newCase: false, retreatment: false, negative: false, bc: false, cd: true } })} className={styles.radioInput} /> CD</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          <div className={styles.formSection} style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>TB Assessment Guidelines</h2>
            </div>
            <div className={styles.tbInfoBox}>
                <strong>Presumptive TB if:</strong>
                <ul>
                  <li>Unexplained cough of any duration (A) with/without other signs/symptoms (C) or risk factors (B).</li>
                  <li>X-ray suggestive of TB (D)</li>
                  <li>If with any other signs/symptoms (C) or risk factors (B), refer to physician.</li>
                  <li>In absence of physician, you may consider presumptive TB.</li>
                </ul>
                <strong>Presumptive DR-TB if:</strong>
                <ul>
                  <li>Presumptive TB with previous treatment (E)</li>
                  <li>Presumptive TB with exposure to known active DR-TB case (F)</li>
                </ul>
              </div>
            </div>
          </>
          )}

        </form>
    </div>
  );
}

export default TBScreening; 